import {trackEvent, TrackEventData} from '@hconnect/common/logging/Analytics'
import {ShiftEventCategory, EventType} from '@hconnect/common/types'
import {useRequiredUser} from '@hconnect/login'
import {Page} from '@widgets/shift-event-filters'
import {isObject} from 'lodash'
import {useCallback} from 'react'

type BaseAnalyticsEvent =
  | {
      name: 'onErrorPageTracking'
      data: {errorCode?: string; errorMessage?: string}
    }
  | {
      name: 'onCategoryStoppageCodeEvent'
      data: {stoppageCode?: string; eventType: EventType; category?: ShiftEventCategory}
    }
  | {
      name: 'userReportsAStoppageFromRuntimeGraph'
      data: {stoppageId: string}
    }
  | {
      name: 'userMovesPage'
      data: {pageFrom?: string; pageTo?: string}
    }
  | {
      name: 'userAdjustsFilter'
      data: {filterPage: Page; filterName?: string}
    }
  | {
      name: 'userDownloadsShiftSummaryPDF'
    }
  | {
      name: 'userAddsInstruction'
      data: {instructionId: string}
    }
  | {
      name: 'userEditsInstruction'
      data: {instructionId: string}
    }
  | {
      name: 'userDeletesInstruction'
      data: {instructionId: string}
    }
  | {
      name: 'userOpensInstructionDetails'
      data: {instructionId: string}
    }
  | {
      name: 'userClosesInstructionDetails'
      data: {instructionId: string}
    }

interface CommonAnalyticsData {
  data: {
    plantId?: string
    userId?: string
  }
}

type AnalyticsEvent = BaseAnalyticsEvent & CommonAnalyticsData

export const trackAnalyticsEvent = <T extends AnalyticsEvent['name']>(
  eventName: T,
  data: TrackEventData
) => trackEvent(eventName, {product: 'cockpit', ...data})

type Params = {
  plantId: string
  country: string
}

export const useTrackAnalyticsEvent = ({plantId, country}: Params) => {
  const {id: userId} = useRequiredUser()

  return useCallback(
    <T extends AnalyticsEvent['name']>(
      eventName: T,
      data: Extract<AnalyticsEvent, {name: T}> extends {data: infer D} ? D : Record<string, never>
    ) => {
      if (plantId) {
        trackAnalyticsEvent(eventName, {
          timestamp: new Date().toISOString(),
          userId,
          plantId,
          country,
          ...(isObject(data) ? data : {})
        })
      }
    },
    [plantId, userId, country]
  )
}
